import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { useCallback, useEffect, useRef, useState } from "react";

interface UsePullToRefreshOptions {
	onRefresh: () => Promise<void> | void;
	threshold?: number;
	maxPull?: number;
	enabled?: boolean;
}

export function usePullToRefresh({
	onRefresh,
	threshold = 80,
	maxPull = 150,
	enabled = true,
}: UsePullToRefreshOptions) {
	const [pullDistance, setPullDistance] = useState(0);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const touchStartY = useRef<number>(0);
	const scrollElement = useRef<HTMLElement | null>(null);
	const hasTriggeredHaptic = useRef<boolean>(false);

	const handleTouchStart = useCallback(
		(e: TouchEvent) => {
			if (!enabled || isRefreshing) return;

			// Check if touch started inside a scrollable container (modal or chat)
			const target = e.target as HTMLElement;
			const isInsideScrollableContainer = target.closest(
				'[data-scrollable="true"]',
			);

			// Don't allow pull-to-refresh if touch started inside a scrollable container
			if (isInsideScrollableContainer) return;

			// Only allow pull-to-refresh when scrolled to top
			const element = scrollElement.current || document.documentElement;
			if (element.scrollTop === 0) {
				touchStartY.current = e.touches[0].clientY;
			}
		},
		[enabled, isRefreshing],
	);

	const handleTouchMove = useCallback(
		async (e: TouchEvent) => {
			if (!enabled || isRefreshing || touchStartY.current === 0) return;

			const currentY = e.touches[0].clientY;
			const diff = currentY - touchStartY.current;

			// Only allow pull down
			if (diff > 0) {
				// Prevent default scroll behavior during pull
				e.preventDefault();

				// Apply resistance to the pull (diminishing returns)
				const resistance = 0.5;
				const pullAmount = Math.min(diff * resistance, maxPull);
				setPullDistance(pullAmount);

				// Trigger haptic feedback when threshold is reached
				if (pullAmount >= threshold && !hasTriggeredHaptic.current) {
					try {
						await Haptics.impact({ style: ImpactStyle.Medium });
						hasTriggeredHaptic.current = true;
					} catch (error) {
						console.warn("Haptics not available:", error);
					}
				}
			}
		},
		[enabled, isRefreshing, threshold, maxPull],
	);

	const handleTouchEnd = useCallback(async () => {
		if (!enabled || isRefreshing || touchStartY.current === 0) return;

		// Trigger refresh if pulled past threshold
		if (pullDistance >= threshold) {
			setIsRefreshing(true);

			try {
				// Trigger strong haptic feedback on release
				await Haptics.impact({ style: ImpactStyle.Heavy });
			} catch (error) {
				console.warn("Haptics not available:", error);
			}

			try {
				await onRefresh();
			} catch (error) {
				console.error("Refresh error:", error);
			} finally {
				setIsRefreshing(false);
				setPullDistance(0);
				touchStartY.current = 0;
				hasTriggeredHaptic.current = false;
			}
		} else {
			// Reset without refreshing
			setPullDistance(0);
			touchStartY.current = 0;
			hasTriggeredHaptic.current = false;
		}
	}, [enabled, isRefreshing, pullDistance, threshold, onRefresh]);

	// Set up event listeners
	useEffect(() => {
		if (!enabled) return;

		const options: AddEventListenerOptions = { passive: false };

		document.addEventListener("touchstart", handleTouchStart, options);
		document.addEventListener("touchmove", handleTouchMove, options);
		document.addEventListener("touchend", handleTouchEnd, options);

		return () => {
			document.removeEventListener("touchstart", handleTouchStart);
			document.removeEventListener("touchmove", handleTouchMove);
			document.removeEventListener("touchend", handleTouchEnd);
		};
	}, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);

	return {
		pullDistance,
		isRefreshing,
		isPulling: pullDistance > 0,
		isThresholdReached: pullDistance >= threshold,
	};
}
